import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../../web/src/componentes/base';
import { Carregando } from '../../../web/src/componentes/estados';
import { Entrada, Rotulo } from '../../../web/src/componentes/formulario';
import { ErroRequisicao, mensagemDeErro } from '../../../web/src/nucleo/api';
import { chamar, pget, ppost } from '../api';
import { destinoDaEntrada } from '../destino';
import { cofre, ehNativo } from '../nativo';
import { CHAVE_SESSAO } from '../sessao';
import { Fingerprint } from 'lucide-react';
import { entrarComPasskey, temPasskey } from '../passkey';
import { FaixaAmbiente, Marca } from './comum';

/** Fragmento do endereço (`#token`): o token do e-mail nunca vai ao servidor no GET nem ao Referer. */
function tokenDoFragmento(hash: string): string | null {
  const t = hash.replace(/^#/, '');
  return /^[A-Za-z0-9_-]{43}$/.test(t) ? t : null;
}

/** Ambiente do endereço (Sandbox ou não), sem sessão: a faixa das telas públicas (spec r23, O4). */
export const CHAVE_AMBIENTE = ['portal', 'ambiente'];

/** Moldura das telas de entrada: a mesma composição do login do MalgoOS, com a marca do app do cliente. */
export function Moldura({ titulo, children }: { titulo: string; children: ReactNode }) {
  const { t } = useTranslation('portal');
  const ambiente = useQuery({ queryKey: CHAVE_AMBIENTE, queryFn: () => pget<{ sandbox: boolean }>('/acesso/ambiente'), staleTime: Infinity, retry: false });
  return (
    <div className="grid min-h-dvh pt-seguro pb-seguro lg:grid-cols-2">
      <FaixaAmbiente avisos={ambiente.data?.sandbox ? [t('faixa.sandbox')] : []} />
      <section className="hidden flex-col justify-between border-r border-borda bg-sup-1 p-12 lg:flex">
        <Marca grande />
        <div className="max-w-md">
          <p className="kicker">{t('app.subtitulo')}</p>
          <h1 className="mt-3 text-4xl leading-tight font-semibold tracking-tight">{t('app.titulo')}</h1>
          <p className="mt-4 text-tinta-2">{t('app.texto')}</p>
        </div>
        <p className="numero text-xs text-tinta-3">malgo.com.br</p>
      </section>
      <section className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Marca />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">{titulo}</h2>
          <div className="mt-6">{children}</div>
        </div>
      </section>
    </div>
  );
}

export function Aviso({ tipo, id, children }: { tipo: 'erro' | 'ok'; id?: string; children: ReactNode }) {
  return (
    <p id={id} role={tipo === 'erro' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2 text-sm ${tipo === 'erro' ? 'border-perigo text-perigo' : 'border-verde text-verde'}`}>
      {children}
    </p>
  );
}

/** Depois de entrar: no app nativo o token vai ao armazenamento seguro; no navegador o cookie já veio. */
async function concluirEntrada(r: { token?: string } | undefined) {
  if (ehNativo() && r?.token) await cofre.gravarToken(r.token);
}

function Lembrar({ marcado, aoMudar }: { marcado: boolean; aoMudar: (v: boolean) => void }) {
  const { t } = useTranslation('portal');
  return (
    <label className="flex items-center gap-2 text-sm text-tinta-2">
      <input type="checkbox" className="size-4" checked={marcado} onChange={(e) => aoMudar(e.target.checked)} />
      {t('entrar.lembrar')}
    </label>
  );
}

/** Entrar: e-mail, código de 6 dígitos (ou o link do e-mail neste aparelho) e, opcional, a senha. */
export function Entrar() {
  const { t } = useTranslation('portal');
  const local = useLocation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const link = tokenDoFragmento(local.hash);
  const [modo, setModo] = useState<'email' | 'codigo' | 'senha'>('email');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const refErro = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (erro) refErro.current?.focus();
  }, [erro]);

  async function entrou(r: { token?: string } | undefined) {
    await concluirEntrada(r);
    await qc.invalidateQueries({ queryKey: CHAVE_SESSAO });
    // Review r52 (VIS-M2, SPEC-B2): volta ao destino de quem chegou por um link (a fatura da cobrança), só caminho interno.
    navegar(destinoDaEntrada(local.state), { replace: true });
  }

  function traduzir(e: unknown) {
    if (e instanceof ErroRequisicao && e.status === 429) return t('entrar.bloqueado');
    if (e instanceof ErroRequisicao && e.corpo.code === 'portal.codigo_invalido') return t('entrar.codigoInvalido');
    if (e instanceof ErroRequisicao && e.corpo.code === 'portal.credenciais_invalidas') return t('entrar.credenciais');
    if (e instanceof ErroRequisicao && e.corpo.code === 'portal.link_outro_aparelho') return t('entrar.linkOutroAparelho');
    if (e instanceof ErroRequisicao && e.corpo.code === 'portal.link_invalido') return t('entrar.linkInvalido');
    if (e instanceof ErroRequisicao && e.corpo.code === 'portal.passkey_invalida') return t('entrar.passkeyErro');
    return mensagemDeErro(e);
  }

  async function executar(fn: () => Promise<void>) {
    setErro(null);
    setOk(null);
    setEnviando(true);
    try {
      await fn();
    } catch (e) {
      setErro(traduzir(e));
    } finally {
      setEnviando(false);
    }
  }

  const pedir = (e?: FormEvent) => {
    e?.preventDefault();
    return executar(async () => {
      const r = await ppost<{ vinculo?: string }>('/acesso/pedir', { email, idioma: document.documentElement.lang.slice(0, 2) });
      if (ehNativo() && r?.vinculo) await cofre.gravarVinculo(r.vinculo);
      setOk(t('entrar.enviado'));
      setModo('codigo');
    });
  };

  if (link) {
    return (
      <Moldura titulo={t('entrar.linkTitulo')}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-tinta-2">{t('entrar.linkTexto')}</p>
          {erro && (
            <p ref={refErro} tabIndex={-1} role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
              {erro}
            </p>
          )}
          <Lembrar marcado={lembrar} aoMudar={setLembrar} />
          {/* Só este clique (POST) consome o link: o robô do antivírus que abre o e-mail não queima a entrada. */}
          <Botao
            variante="primario"
            carregando={enviando}
            onClick={() =>
              executar(async () => {
                const vinculo = ehNativo() ? await cofre.lerVinculo() : undefined;
                await entrou(await ppost<{ token?: string }>('/acesso/link', { token: link, lembrar, ...(vinculo ? { vinculo } : {}) }));
              })
            }
          >
            {t('entrar.linkEntrar')}
          </Botao>
          <button type="button" className="text-center text-sm text-tinta-2 hover:text-acento-forte" onClick={() => navegar('/entrar', { replace: true, state: local.state })}>
            {t('entrar.semSenha')}
          </button>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura titulo={t('entrar.titulo')}>
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (modo === 'email') return void pedir();
          if (modo === 'codigo') return void executar(async () => entrou(await ppost<{ token?: string }>('/acesso/codigo', { email, codigo: codigo.replace(/\D/g, ''), lembrar })));
          return void executar(async () => entrou(await ppost<{ token?: string }>('/acesso/senha', { email, senha, lembrar })));
        }}
      >
        <p className="text-sm text-tinta-2">{t('entrar.texto')}</p>
        {ok && <Aviso tipo="ok">{ok}</Aviso>}
        {erro && (
          <p ref={refErro} tabIndex={-1} id="entrar-erro" role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
        <Rotulo id="email" rotulo={t('entrar.email')}>
          <Entrada id="email" type="email" inputMode="email" autoComplete="username" required value={email} readOnly={modo === 'codigo'} onChange={(e) => setEmail(e.target.value)} />
        </Rotulo>
        {modo === 'codigo' && (
          <Rotulo id="codigo" rotulo={t('entrar.codigo')}>
            <Entrada id="codigo" className="numero tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={7} autoFocus value={codigo} aria-describedby={erro ? 'entrar-erro' : undefined} onChange={(e) => setCodigo(e.target.value)} />
          </Rotulo>
        )}
        {modo === 'senha' && (
          <Rotulo id="senha" rotulo={t('entrar.senha')}>
            <Entrada id="senha" type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} />
          </Rotulo>
        )}
        {modo !== 'email' && <Lembrar marcado={lembrar} aoMudar={setLembrar} />}
        <Botao type="submit" variante="primario" carregando={enviando} disabled={!email.includes('@') || (modo === 'codigo' && codigo.replace(/\D/g, '').length !== 6) || (modo === 'senha' && !senha)}>
          {modo === 'email' ? t('entrar.pedir') : t('entrar.confirmar')}
        </Botao>
        {modo === 'codigo' && (
          <div className="flex flex-wrap justify-between gap-2 text-sm">
            <button type="button" className="text-tinta-2 hover:text-acento-forte" onClick={() => void pedir()}>
              {t('entrar.reenviar')}
            </button>
            <button
              type="button"
              className="text-tinta-2 hover:text-acento-forte"
              onClick={() => {
                setModo('email');
                setCodigo('');
                setOk(null);
              }}
            >
              {t('entrar.trocarEmail')}
            </button>
          </div>
        )}
        {modo === 'email' && temPasskey() && (
          <Botao
            icone={<Fingerprint className="size-4" aria-hidden />}
            onClick={() =>
              executar(async () => {
                try {
                  await entrou(await entrarComPasskey(lembrar));
                } catch (e) {
                  // Cancelado no aparelho: nada a dizer; o resto vira o aviso de passkey não reconhecida.
                  if (e instanceof DOMException || (e instanceof Error && e.message === 'cancelado')) return;
                  throw e;
                }
              })
            }
          >
            {t('entrar.passkey')}
          </Botao>
        )}
        <button
          type="button"
          className="text-center text-sm text-tinta-2 hover:text-acento-forte"
          onClick={() => {
            setModo(modo === 'senha' ? 'email' : 'senha');
            setErro(null);
            setOk(null);
          }}
        >
          {modo === 'senha' ? t('entrar.semSenha') : t('entrar.comSenha')}
        </button>
        <p className="text-center text-xs text-tinta-3">{t('entrar.golpe')}</p>
      </form>
    </Moldura>
  );
}

interface DadosConvite {
  nome: string;
  email: string;
  empresa: string;
  escritorio: string;
  termo: { id: string; versao: string; titulo: string; texto: string } | null;
}

/** Convite: empresa e escritório, o termo vigente (aceite registrado) e "Aceitar e entrar", sem criar senha. */
export function Convite() {
  const { t } = useTranslation('portal');
  const local = useLocation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const token = tokenDoFragmento(local.hash);
  const q = useQuery({ queryKey: ['portal', 'convite', token], queryFn: () => pget<DadosConvite>(`/acesso/convite/${token}`), enabled: !!token, retry: false });
  const [aceito, setAceito] = useState(false);
  const [lembrar, setLembrar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  if (!token || q.isError) {
    return (
      <Moldura titulo={t('convite.titulo')}>
        <Aviso tipo="erro">{t('convite.invalido')}</Aviso>
      </Moldura>
    );
  }
  if (q.isPending) return <Carregando />;
  const c = q.data;
  return (
    <Moldura titulo={t('convite.titulo')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-2">{t('convite.texto', { escritorio: c.escritorio, empresa: c.empresa })}</p>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {c.termo && (
          <div className="flex flex-col gap-2">
            <p className="kicker">
              {c.termo.titulo} · {t('termo.versao', { versao: c.termo.versao })}
            </p>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-borda bg-sup-2 p-3 text-sm whitespace-pre-wrap text-tinta-2" tabIndex={0} aria-label={t('convite.termo')}>
              {c.termo.texto}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4" checked={aceito} onChange={(e) => setAceito(e.target.checked)} />
              {t('convite.aceito')}
            </label>
          </div>
        )}
        <Lembrar marcado={lembrar} aoMudar={setLembrar} />
        <Botao
          variante="primario"
          carregando={enviando}
          disabled={!!c.termo && !aceito}
          onClick={async () => {
            setErro(null);
            setEnviando(true);
            try {
              const r = await chamar<{ token?: string }>('POST', `/acesso/convite/${token}/aceitar`, { termo_id: c.termo?.id ?? null, lembrar });
              await concluirEntrada(r);
              await qc.invalidateQueries({ queryKey: CHAVE_SESSAO });
              // Review r52 (VIS-M2): o convite também volta ao destino guardado (só caminho interno).
              navegar(destinoDaEntrada(local.state), { replace: true });
            } catch (e) {
              setErro(e instanceof ErroRequisicao && e.corpo.code === 'portal.convite_invalido' ? t('convite.invalido') : mensagemDeErro(e));
            } finally {
              setEnviando(false);
            }
          }}
        >
          {t('convite.entrar')}
        </Botao>
        <p className="text-center text-xs text-tinta-3">{t('entrar.golpe')}</p>
      </div>
    </Moldura>
  );
}

/** "Ver como o cliente": a equipe chega com o token de 2 minutos no fragmento; a sessão é só de leitura. */
export function VerComo() {
  const { t } = useTranslation('portal');
  const local = useLocation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const token = tokenDoFragmento(local.hash);
  const [erro, setErro] = useState(false);
  const feito = useRef(false);
  useEffect(() => {
    if (!token || feito.current) return;
    feito.current = true;
    void ppost<{ token?: string }>('/acesso/ver-como', { token })
      .then(async (r) => {
        await concluirEntrada(r);
        await qc.invalidateQueries({ queryKey: CHAVE_SESSAO });
        navegar('/', { replace: true });
      })
      .catch(() => setErro(true));
  }, [token, navegar, qc]);
  return <Moldura titulo={t('verComo.titulo')}>{!token || erro ? <Aviso tipo="erro">{t('verComo.invalido')}</Aviso> : <Carregando />}</Moldura>;
}
